import type { BUITheme } from '@blofin/blofin-ui';
import AnnouncementSection from 'components/home/AnnouncementSection';
import FAQSection from 'components/home/FAQSection';
import HeroSection from 'components/home/HeroSection';
import SelfServiceSection from 'components/home/SelfServiceSection';
import SupportSection from 'components/home/SupportSection';
import PageLayout from 'components/layout/PageLayout';
import { SELF_SERVICE_ITEMS, SUPPORT_CHANNELS } from 'constants/help-center';
import type { GetServerSideProps } from 'next';
import { useTranslations } from 'next-intl';
import Head from 'next/head';
import React from 'react';
import { getHomepage, getSections } from 'services/help-center';
import {
  homepageSectionToFAQCategory,
  sectionsToAnnouncementCategories,
  topQuestionsToFAQCategory,
} from 'services/help-center/utils';
import { requireMessages } from 'services/http/requireMessages';
import type { AnnouncementCategory, FAQCategory } from 'types/help-center';
import BlofinNative from 'sdk';
import { helper } from '@blofin/blofin-utils';

interface HomeProps {
  theme: BUITheme;
  faqCategories: FAQCategory[];
  announcementCategories: AnnouncementCategory[];
  messages: Record<string, unknown>;
  isApp: boolean;
  isMobile: boolean;
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async (ctx) => {
  const cookies = ctx.req.headers.cookie || '';
  const themeMatch = cookies.match(/(?:^|;\s*)theme=(light|dark)/);
  const theme = (themeMatch?.[1] as BUITheme) || 'light';
  const ua = ctx.req.headers['user-agent'] || '';
  const isApp = BlofinNative.isApp(ua);
  const isMobileFlag = helper.isMobile(ua);
  const language = ctx.locale || 'en';

  let faqCategories: FAQCategory[] = [];
  let announcementCategories: AnnouncementCategory[] = [];
  let messages: Record<string, unknown> = {};

  try {
    const [homepageResult, sectionsResult, messagesResult] = await Promise.allSettled([
      getHomepage(language),
      getSections({ language, categoryCode: 'announcement' }),
      requireMessages(['common', 'components', 'support'], ctx.locale),
    ]);

    if (messagesResult.status === 'fulfilled') {
      messages = messagesResult.value;
    }

    if (homepageResult.status === 'fulfilled' && homepageResult.value?.code === 200) {
      const homepage = homepageResult.value.data;
      const items: FAQCategory[] = [];
      if (homepage?.topQuestions?.length) {
        items.push(topQuestionsToFAQCategory(homepage.topQuestions));
      }
      if (homepage?.faqSections) {
        items.push(...homepage.faqSections.map((s) => homepageSectionToFAQCategory(s)));
      }
      faqCategories = items;
    }

    if (sectionsResult.status === 'fulfilled' && sectionsResult.value?.code === 200) {
      announcementCategories = sectionsToAnnouncementCategories(sectionsResult.value.data ?? []);
    }
  } catch (error) {
    console.log('[Home] getServerSideProps error:', error);
  }

  return { props: { theme, faqCategories, announcementCategories, messages, isApp, isMobile: isMobileFlag } };
};

export default function Home({
  theme,
  faqCategories,
  announcementCategories,
  isApp,
  isMobile: isMobileSSR,
}: HomeProps) {
  const t = useTranslations('support');
  return (
    <PageLayout theme={theme} headerTheme="dark" showTheme hideHeader={isApp} hideFooter={isApp}>
      <Head>
        <title>{t('seoIndexTitle')}</title>
        <meta name="description" content={t('seoIndexDesc')} />
      </Head>
      <HeroSection />
      <SelfServiceSection items={SELF_SERVICE_ITEMS} isMobile={isMobileSSR} />
      <FAQSection categories={faqCategories} />
      <AnnouncementSection categories={announcementCategories} />
      <SupportSection channels={SUPPORT_CHANNELS} />
    </PageLayout>
  );
}
